import { useState } from "react";
import { Layout } from "@/components/Layout";
import { PlanGate } from "@/components/PlanGate";
import { REQUIRES } from "@/lib/plans";
import { useAuth } from "@/contexts/AuthContext";
import { getLevel } from "@/lib/plans";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  FOOD_INTRO, MACROS, CALORIC_STATES, MEAL_TEMPLATES,
  HYDRATION, SUBSTITUTIONS, PRACTICAL_TIPS, FOOD_FAQ,
} from "@/data/foodGuide";
import {
  ForkKnife, Barbell, Lightning, Fire, Drop, BowlFood, Clock, CheckCircle,
  Warning, Question, DownloadSimple, Lock, CircleNotch, ArrowsClockwise, Info,
} from "@phosphor-icons/react";

const MACRO_ICONS = { protein: Barbell, carbs: Lightning, fats: Fire };
const MEAL_ICONS = { breakfast: BowlFood, lunch: ForkKnife, dinner: ForkKnife, snack: BowlFood, post_workout: Barbell, bedtime: Clock };

export default function FoodGuide() {
  return (
    <Layout>
      <PlanGate required={REQUIRES.SUPPLEMENTS_GUIDE} planName="AS START">
        {/* HERO */}
        <section className="border-b border-as-border bg-as-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <p className="as-label text-as-green mb-3">GUÍA DE ALIMENTACIÓN</p>
            <h1 className="as-heading text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="food-guide-title">
              COMER PARA <span className="text-as-green">TRANSFORMAR.</span>
            </h1>
            <p className="text-as-gray mt-4 max-w-2xl">
              Principios de nutrición deportiva y planeación de comidas. Base científica sin dietas restrictivas.
              Estrategia real para cualquier objetivo.
            </p>
            <div className="mt-6"><DownloadFoodGuideBtn /></div>
          </div>
        </section>

        {/* PRINCIPLES */}
        <section className="bg-as-surface py-12 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <Info size={22} weight="duotone" color="#39FF14" />
              <p className="as-label text-as-green">FUNDAMENTOS</p>
            </div>
            <h2 className="as-heading text-3xl text-white mb-8">6 PRINCIPIOS DE NUTRICIÓN DEPORTIVA</h2>
            <p className="text-as-gray max-w-3xl leading-relaxed mb-8">{FOOD_INTRO.what_is}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FOOD_INTRO.principles.map((p, i) => (
                <div key={i} className="bg-as-black border-l-2 border-as-green p-5">
                  <p className="as-label text-as-green text-[10px] mb-1">#{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="as-heading text-base text-white mb-2">{p.title}</h3>
                  <p className="text-xs text-as-gray leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MACROS */}
        <section className="bg-as-black py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="as-label text-as-green mb-2">MACRONUTRIENTES</p>
            <h2 className="as-heading text-3xl text-white mb-8">CADA GRAMO CUENTA</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {MACROS.map((m) => {
                const Icon = MACRO_ICONS[m.id] || Fire;
                return (
                  <div key={m.id} className="bg-as-surface border border-as-border p-6" data-testid={`macro-${m.id}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-as-black border border-as-green/40 flex items-center justify-center">
                        <Icon size={22} weight="duotone" color="#39FF14" />
                      </div>
                      <div>
                        <h3 className="as-heading text-xl text-white">{m.name.toUpperCase()}</h3>
                        <p className="text-[10px] text-as-gray uppercase tracking-widest">{m.kcal_per_gram} kcal / g</p>
                      </div>
                    </div>
                    <p className="text-xs text-as-gray leading-relaxed mb-4">{m.what}</p>
                    <div className="mb-4">
                      <p className="as-label text-as-green text-[10px] mb-2">DOSIS DIARIA</p>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <div className="bg-as-black p-2 border border-as-border">
                          <p className="text-as-gray">Sedentario</p>
                          <p className="text-white font-bold">{m.daily_needs.sedentary}</p>
                        </div>
                        <div className="bg-as-black p-2 border border-as-border">
                          <p className="text-as-gray">Activo</p>
                          <p className="text-white font-bold">{m.daily_needs.active}</p>
                        </div>
                        <div className="bg-as-black p-2 border border-as-border">
                          <p className="text-as-gray">Deportista</p>
                          <p className="text-white font-bold">{m.daily_needs.athlete}</p>
                        </div>
                        <div className="bg-as-black p-2 border border-as-green">
                          <p className="text-as-green">Definición</p>
                          <p className="text-white font-bold">{m.daily_needs.cutting}</p>
                        </div>
                      </div>
                    </div>
                    <SourcesBlock title="ALTAS" items={m.sources_high || m.sources_slow || m.sources_mono} />
                    <SourcesBlock title="MEDIAS" items={m.sources_medium || m.sources_fast || m.sources_poly} />
                    <SourcesBlock title={m.id === "protein" ? "VEGETALES" : m.id === "carbs" ? "FIBRA" : "SATURADAS"} items={m.sources_plant || m.sources_fiber || m.sources_saturated} />
                    <div className="mt-4 pt-3 border-t border-as-border">
                      <p className="as-label text-as-green text-[10px] mb-2">TIPS</p>
                      <ul className="space-y-1">
                        {m.tips.map((t, i) => (
                          <li key={i} className="text-xs text-white flex gap-2 leading-relaxed">
                            <CheckCircle size={12} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CALORIC STATES */}
        <section className="bg-as-surface py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="as-label text-as-green mb-2">BALANCE CALÓRICO</p>
            <h2 className="as-heading text-3xl text-white mb-8">TU OBJETIVO DETERMINA TU CANTIDAD</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {CALORIC_STATES.map((c) => (
                <div key={c.id} className={`bg-as-black border p-6 ${c.color === "green" ? "border-as-green" : "border-as-border"}`}>
                  <p className={`as-label text-[10px] mb-1 ${c.color === "green" ? "text-as-green" : "text-as-gray"}`}>{c.subtitle}</p>
                  <h3 className="as-heading text-xl text-white mb-3">{c.label.toUpperCase()}</h3>
                  <p className="text-3xl font-black text-as-green mb-1">{c.adjustment}</p>
                  <p className="text-xs text-as-gray mb-4">{c.example}</p>
                  <div className="pt-3 border-t border-as-border">
                    <p className="text-[10px] uppercase tracking-widest text-as-green mb-1">PROTEÍNA</p>
                    <p className="text-xs text-white">{c.protein_hint}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MEAL TEMPLATES */}
        <section className="bg-as-black py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="as-label text-as-green mb-2">PLANEACIÓN DE COMIDAS</p>
            <h2 className="as-heading text-3xl text-white mb-8">FÓRMULAS PARA CADA COMIDA</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {MEAL_TEMPLATES.map((meal) => {
                const Icon = MEAL_ICONS[meal.id] || ForkKnife;
                return (
                  <div key={meal.id} className="bg-as-surface border border-as-border p-5" data-testid={`meal-${meal.id}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={20} weight="duotone" color="#39FF14" />
                      <h3 className="as-heading text-lg text-white">{meal.name.toUpperCase()}</h3>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-as-gray mb-3">
                      <Clock size={12} weight="bold" /> {meal.when}
                    </div>
                    <div className="p-3 bg-as-black border border-as-green/40 mb-3">
                      <p className="text-[10px] uppercase tracking-widest text-as-green mb-1">FÓRMULA</p>
                      <p className="text-xs text-white leading-relaxed">{meal.formula}</p>
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-as-green mb-2">EJEMPLOS</p>
                    <ul className="space-y-1.5">
                      {meal.examples.map((ex, i) => (
                        <li key={i} className="text-xs text-white flex gap-2 leading-relaxed">
                          <span className="text-as-green flex-shrink-0">▸</span> {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HYDRATION */}
        <section className="bg-as-surface py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <Drop size={22} weight="duotone" color="#39FF14" />
              <p className="as-label text-as-green">HIDRATACIÓN</p>
            </div>
            <h2 className="as-heading text-3xl text-white mb-8">EL 60% DE TI ES AGUA</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-as-black border-l-2 border-as-green p-5">
                <p className="as-label text-as-green text-[10px] mb-1">DIARIA BASE</p>
                <p className="text-sm text-white">{HYDRATION.daily_base}</p>
              </div>
              <div className="bg-as-black border-l-2 border-as-green p-5">
                <p className="as-label text-as-green text-[10px] mb-1">ENTRENANDO</p>
                <p className="text-sm text-white">{HYDRATION.workout_add}</p>
              </div>
              <div className="bg-as-black border-l-2 border-as-green p-5">
                <p className="as-label text-as-green text-[10px] mb-1">CLIMA CÁLIDO</p>
                <p className="text-sm text-white">{HYDRATION.hot_climate}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5 mt-5">
              <div className="bg-as-black p-5 border border-as-border">
                <p className="as-label text-[10px] text-red-400 mb-3">SEÑALES DE DESHIDRATACIÓN</p>
                <ul className="space-y-1.5">
                  {HYDRATION.signs_low.map((s, i) => (
                    <li key={i} className="text-xs text-white flex gap-2">
                      <Warning size={12} weight="bold" color="#FF3333" className="flex-shrink-0 mt-0.5" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-as-black p-5 border border-as-border">
                <p className="as-label text-as-green text-[10px] mb-3">CÓMO IMPLEMENTARLO</p>
                <ul className="space-y-1.5">
                  {HYDRATION.tips.map((t, i) => (
                    <li key={i} className="text-xs text-white flex gap-2">
                      <CheckCircle size={12} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SUBSTITUTIONS */}
        <section className="bg-as-black py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <ArrowsClockwise size={22} weight="duotone" color="#39FF14" />
              <p className="as-label text-as-green">SUSTITUCIONES</p>
            </div>
            <h2 className="as-heading text-3xl text-white mb-8">CAMBIA SIN PERDER OBJETIVO</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {SUBSTITUTIONS.map((sub, i) => (
                <div key={i} className="bg-as-surface p-4 border border-as-border grid grid-cols-[110px_1fr] items-center gap-4">
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-as-green mb-1">{sub.group}</p>
                    <p className="text-sm text-white font-bold">{sub.from}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-as-gray mb-1">Sustitutos válidos</p>
                    <p className="text-xs text-white leading-relaxed">{sub.to.join(" · ")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRACTICAL TIPS */}
        <section className="bg-as-surface py-14 border-b border-as-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="as-label text-as-green mb-2">HÁBITOS Y PLANEACIÓN</p>
            <h2 className="as-heading text-3xl text-white mb-8">EJECUTA COMO PRO</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {PRACTICAL_TIPS.map((block, i) => (
                <div key={i} className="bg-as-black border border-as-border p-5">
                  <h3 className="as-heading text-lg text-as-green mb-3">{block.title.toUpperCase()}</h3>
                  <ul className="space-y-2">
                    {block.items.map((t, j) => (
                      <li key={j} className="text-xs text-white flex gap-2 leading-relaxed">
                        <span className="text-as-green flex-shrink-0">▸</span> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-as-black py-14 border-b border-as-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="as-label text-as-green mb-2">PREGUNTAS FRECUENTES</p>
            <h2 className="as-heading text-3xl text-white mb-8 flex items-center gap-2">
              <Question size={26} weight="duotone" color="#39FF14" /> DUDAS COMUNES
            </h2>
            <FoodFaq />
          </div>
        </section>
      </PlanGate>
    </Layout>
  );
}

function SourcesBlock({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3">
      <p className="as-label text-as-green text-[10px] mb-1">{title}</p>
      <p className="text-[11px] text-white leading-relaxed">{items.join(" · ")}</p>
    </div>
  );
}

function FoodFaq() {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2">
      {FOOD_FAQ.map((f, i) => (
        <div key={i} className="bg-as-surface border border-as-border">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full text-left px-4 py-3 flex justify-between items-center gap-2 hover:bg-as-black transition-colors"
            data-testid={`food-faq-${i}`}
          >
            <span className="text-sm text-white font-bold">{f.q}</span>
            <span className="text-as-green flex-shrink-0">{open === i ? "−" : "+"}</span>
          </button>
          {open === i && (
            <div className="px-4 pb-4 pt-1 text-xs text-as-gray leading-relaxed border-t border-as-border">
              {f.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DownloadFoodGuideBtn() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const level = getLevel(user);
  const canDownload = level >= 2;

  const handleDownload = async () => {
    if (!canDownload) {
      toast.error("Función exclusiva ELITE / EVOLUTION", {
        description: user ? "Actualiza tu plan para descargar la guía completa" : "Inicia sesión con plan ELITE o EVOLUTION",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/food-guide/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intro: FOOD_INTRO,
          macros: MACROS,
          caloric_states: CALORIC_STATES,
          meal_templates: MEAL_TEMPLATES,
          hydration: HYDRATION,
          substitutions: SUBSTITUTIONS,
          practical_tips: PRACTICAL_TIPS,
          faq: FOOD_FAQ,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error" }));
        throw new Error(err.detail || "Error al generar PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guia_alimentacion_AS_TRAINING.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Guía descargada");
    } catch (e) {
      toast.error(e.message || "No pudimos generar el PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
        canDownload ? "bg-as-green text-as-black hover:bg-white" : "bg-as-surface text-as-gray border border-as-border hover:border-as-green"
      } disabled:opacity-60`}
      data-testid="pdf-download-food"
    >
      {loading ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : canDownload ? <DownloadSimple size={16} weight="bold" /> : <Lock size={16} weight="bold" />}
      {loading ? "Generando PDF..." : canDownload ? "Descargar guía (PDF)" : "PDF ELITE / EVOLUTION"}
    </button>
  );
}
